import React, { useEffect, useCallback, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupString,
  YupBoolean,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import * as Yup from 'yup';
import * as Lo from 'lodash';
import {
  gqlGetByIdQuery,
  gqlAdd,
  gqlUpdate,
  gqlDelete,
  FormInput,
  RequestLineInput,
} from '../graphql';
import '../../../../styles/tab-styles.css';
import { FormFields } from './collectionpointRequest-form-fields';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import { ButtonCloseModal } from 'shared-pages/components';
import { getRouteByName } from 'app/routes';
import {
  isAfisCyprus,
  isFotokiklosi,
  isMedicycle,
  requiredString,
} from 'shared-pages/common/constants';

type Props = {
  isModal?: boolean;
  paramId?: string | undefined;
  modalOnClose?: (id: number) => void | undefined;
};

export const CollectionPointRequestFormPage = ({
  isModal = false,
  paramId = undefined,
  modalOnClose,
}: Props) => {
  const { handleGraphqlError } = useHandleError('collectionpointrequest');
  const { appStore } = useRootStore();
  const history = useHistory();
  const { id: urlId } = useParams<{ id: string }>();
  const id = isModal ? paramId : urlId;
  const idNum = id ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const { t } = useTranslation();

  // Customize FormShape based on Customer/Company
  function useCustomFormShape(base: any) {
    const { t } = useTranslation();
    const reqStr = t(requiredString);

    let formShape = Yup.object(base);

    // Fotokiklosi
    if (isFotokiklosi) {
      formShape = formShape.concat(
        Yup.object({
          addressNomos: YupString.required(reqStr),
        }),
      );
    }

    return formShape;
  }

  // Validation Schema
  const formShape = {
    id: YupNumeric,
    name: YupString.required(t('Το Όνομα είναι υποχρεωτικό')), // Required
    professionId: YupNumeric,
    addressLine1: YupString,
    addressArea: YupString,
    postalCode: YupString,
    addressCity: YupString,
    addressNomos: YupString,
    phoneNumber1: YupString.required(t('Το Τηλέφωνο είναι υποχρεωτικό')),
    phoneNumber2: YupString.nullable(),
    companyVat: YupString.required(t('Το ΑΦΜ είναι υποχρεωτικό')), // Required
    companyTaxOffice:
      !isAfisCyprus && !isMedicycle ? YupString.required(t('Η ΔΟΥ είναι υποχρεωτική')) : YupString, // Required
    contactPerson: YupString,
    email: YupString.email(),
    notes: YupString,
    isApproved: YupBoolean.nullable(),
    judgeNotes: YupString.nullable(),
    judgedByUser: YupString.nullable(),
    judgeDate: Yup.date()
      .nullable()
      .typeError('Η τιμή πρέπει να είναι ημερομηνία')
      .test('valid-format', 'Μη έγκυρη μορφή ημερομηνίας (YYYY-MM-DD)', (value) => {
        // If the value is null or undefined, bypass the test.
        if (value == null) return true;
        // Otherwise, perform the validation.
        return value instanceof Date && !isNaN(value.getTime());
      }),
    collectionpointId: YupNumeric.nullable(),
    collectionpoint: Yup.object()
      .shape({
        id: YupNumeric,
        name: YupString,
      })
      .nullable(),
    requestLines: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          containerTypeId: Yup.number().required('Το Απόβλητο είναι υποχρεωτικό'), // Required
          containerType: Yup.object()
            .shape({
              id: Yup.number().nullable(),
              name: Yup.string().nullable(),
            })
            .nullable(),
          quantity: Yup.number()
            .moreThan(0, 'Η ποσότητα πρέπει να είναι μεγαλύτερη από 0') // Quantity > 0
            .required('Η ποσότητα είναι υποχρεωτική'), // Required
        }),
      )
      .min(1, 'Πρέπει να προσθέσετε τουλάχιστον ένα Τύπο Συσκευασίας στην Αίτηση'),
  };

  // const validationSchema = Yup.object().shape(formShape);
  const validationSchema = useCustomFormShape(formShape);
  const pickProperties = (values) => Lo.pick(values, Lo.keys(formShape));
  type FormValuesType = Yup.InferType<typeof validationSchema>;

  const [addNewRecord] = useMutation(gqlAdd);
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery);
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const [initialNewRecData, setInitialNewRecData] = useState<any>();

  /** Fetch data or initialize new record */
  useEffect(() => {
    if (isNew) {
      initializeNewRecord();
    } else {
      get({ variables: { id: idNum } });
    }
  }, [get, idNum, isNew]);

  /** Initialize New Record */
  const initializeNewRecord = async () => {
    let newRec: any = { isApproved: null, requestLines: [] };

    setInitialNewRecData(newRec);
  };

  /**
   * * SAVE - UPDATE/CREATE
   */
  const handleSubmit = async (values: FormValuesType, actions) => {
    actions.setSubmitting(false);

    let mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));
    mutValues.requestLines = mutValues.requestLines.map((line: any) => {
      let newLine = Lo.pick(line, Lo.keys(new RequestLineInput()));
      delete newLine.containerType;
      newLine.quantity = parseInt(newLine.quantity);
      newLine.aa = parseInt(newLine.aa);
      return newLine;
    });

    if (isNew) {
      console.log('[CollectionPointRequest] Add:', mutValues);
      const rec = await addNewRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          const rec = d.data[Object.keys(d.data)[0]];
          toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής[${rec?.id}]`);
          return rec;
        })
        .catch((error) => {
          handleGraphqlError('mutation-add', error);
          return null;
        }); //if rec is not null go to the new record
      if (rec && rec.id) {
        history.push({
          pathname: generatePath(getRouteByName('CollectionpointRequest'), { id: rec.id }),
        });
      }

      if (rec && isModal && modalOnClose) modalOnClose(rec.id);
    } else {
      console.log('[CollectionPointRequest] Update:', mutValues);
      updateRecord({ variables: { data: { ...mutValues, id: idNum } } })
        .then((d) => {
          toastSuccess(`Επιτυχημένη Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
          refetch();
        })
        .catch((error) => {
          handleGraphqlError('mutation-update', error);
        })
        .finally(() => {
          refetch();
        });
    }
  };

  /**
   * * DELETE
   */
  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: 'Διαγραφή Αίτησης',
      text: 'Είστε σίγουροι οτι θέλετε να διαγράψετε την αίτηση;',
    });
    if (res) {
      deleteRecord({ variables: { id: idNum } })
        .then(() => history.goBack())
        .catch((error) => {
          handleGraphqlError('mutation-delete', error);
        });
    }
  }, [deleteRecord, idNum, history]);

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else {
    return (
      <FormCard
        title={
          isNew
            ? `${t('Αίτηση Νέου Σημείου Συλλογής')}`
            : `${t('Αίτηση Σημείου Συλλογής')}: ${data?.name}`
        }
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onReturn={null}
        permitSubmit={false}
        permitDelete={!isNew && !data?.isApproved}
        initialValues={isNew ? initialNewRecData : pickProperties(data)}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          isModal
            ? (formikCtx) => (
                <ButtonCloseModal
                  onClick={() => {
                    if (modalOnClose) {
                      modalOnClose(-1);
                    }
                  }}
                />
              )
            : undefined
        }
      >
        <FormFields isNew={isNew} updateRecord={updateRecord} refetch={refetch} data={data} />
      </FormCard>
    );
  }
};
